import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff, Car } from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../api/client'
import { useAuth } from '../context/AuthContext'

export default function LoginPage() {
  const { login, user } = useAuth()
  const navigate = useNavigate()
  const googleBtnRef = useRef(null)

  const [form, setForm]       = useState({ email: '', password: '' })
  const [showPw, setShowPw]   = useState(false)
  const [loading, setLoading] = useState(false)

  // Redirect if already authenticated
  useEffect(() => {
    if (user) navigate('/', { replace: true })
  }, [user, navigate])

  // Initialise Google Identity Services
  useEffect(() => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
    if (!clientId || !window.google) return

    window.google.accounts.id.initialize({
      client_id: clientId,
      callback:  handleGoogleCredential,
    })

    window.google.accounts.id.renderButton(googleBtnRef.current, {
      theme:  'filled_black',
      size:   'large',
      width:  '100%',
      text:   'signin_with',
      shape:  'rectangular',
    })
  }, [])

  const handleGoogleCredential = async ({ credential }) => {
    setLoading(true)
    try {
      const { data } = await api.post('/auth/google', { credential })
      login(data.accessToken, data.user)
      navigate('/')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Google sign-in failed')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      const { data } = await api.post('/auth/login', form)
      login(data.accessToken, data.user)
      navigate('/')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Sign-in failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-dvh flex bg-[var(--bg-base)]">
      {/* Left panel — branding */}
      <div className="hidden lg:flex flex-col justify-between w-[420px] shrink-0
                      bg-[var(--bg-surface)] border-r border-[var(--border)] p-12">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center shadow-glow">
            <Car size={18} className="text-white" />
          </div>
          <span className="font-display font-bold text-lg text-[var(--text-primary)]">
            Service Tracker
          </span>
        </div>

        <div className="space-y-6">
          <h2 className="font-display text-4xl font-bold text-[var(--text-primary)] leading-tight">
            Keep your vehicles in peak condition.
          </h2>
          <p className="text-[var(--text-secondary)] leading-relaxed">
            Track service history, upcoming maintenance, issues, and repair costs — all in one place, for customers and garages alike.
          </p>
          <div className="space-y-3">
            {[
              'MOT, service, tax, and insurance reminders',
              'Log faults with photos and severity',
              'Approve quotes before work begins',
              'Immutable service history you can trust',
            ].map(point => (
              <div key={point} className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 mt-2 shrink-0" />
                <p className="text-sm text-[var(--text-secondary)]">{point}</p>
              </div>
            ))}
          </div>
        </div>

        <p className="text-[11px] text-[var(--text-muted)]">
          © {new Date().getFullYear()} Car Service Tracker
        </p>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm animate-fade-in">
          <div className="mb-8 lg:hidden flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-indigo-600 flex items-center justify-center">
              <Car size={15} className="text-white" />
            </div>
            <span className="font-display font-bold text-[var(--text-primary)]">Service Tracker</span>
          </div>

          <h1 className="font-display text-2xl font-bold text-[var(--text-primary)] mb-2">
            Sign in
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mb-8">
            Welcome back. Sign in to your account.
          </p>

          {/* Google SSO */}
          <div ref={googleBtnRef} className="mb-4" />

          {import.meta.env.VITE_GOOGLE_CLIENT_ID && (
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-1 h-px bg-[var(--border)]" />
              <span className="text-xs text-[var(--text-muted)]">or</span>
              <div className="flex-1 h-px bg-[var(--border)]" />
            </div>
          )}

          {/* Email / password form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">
                Email
              </label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="you@example.com"
                required
                className="w-full px-3 py-2 text-sm bg-[var(--bg-surface)] border border-[var(--border)]
                           rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)]
                           focus:outline-none focus:border-indigo-500 transition-colors"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPw ? 'text' : 'password'}
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                  required
                  className="w-full px-3 py-2 pr-10 text-sm bg-[var(--bg-surface)] border border-[var(--border)]
                             rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)]
                             focus:outline-none focus:border-indigo-500 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]
                             hover:text-[var(--text-secondary)] transition-colors"
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50
                         text-white text-sm font-medium rounded-lg transition-colors"
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-[var(--text-secondary)]">
            Don&apos;t have an account?{' '}
            <Link to="/register" className="text-indigo-400 hover:text-indigo-300 transition-colors">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
